/**
 * useHierarchyAncestorFields
 *
 * Purpose: A hierarchical list field can hold a value from any level above it
 *          (a Geography field on `fd_country` holding "Europe", an `fd_region`
 *          value). To show such values by name, the levels above each field
 *          have to be loaded too. This walks each field's
 *          `hierarchy_config.parent_fielddef_id` chain and returns the ancestors.
 * Input: fields - view fields carrying `field_definition.hierarchy_config`
 * Output: the ancestor fielddef ids (empty until discovered)
 */

import { useEffect, useState } from 'react';
import { metadataService } from '../services/metadataService';

interface HierarchicalField {
  field_definition?: { hierarchy_config?: { parent_fielddef_id?: string | null } | null } | null;
}

export const useHierarchyAncestorFields = (
  fields: HierarchicalField[] | undefined,
  tenantId: string | undefined,
  enabled: boolean = true
): string[] => {
  const [ancestorFields, setAncestorFields] = useState<string[]>([]);

  useEffect(() => {
    if (!enabled || !fields || !tenantId) {
      setAncestorFields([]);
      return;
    }

    let cancelled = false;

    const discoverAncestors = async () => {
      const ancestors = new Set<string>();
      const fieldDefCache = new Map<string, any>();

      for (const field of fields) {
        let parentId = field.field_definition?.hierarchy_config?.parent_fielddef_id;
        while (parentId && !ancestors.has(parentId)) {
          ancestors.add(parentId);
          // Fetch parent field definition to continue walking up
          try {
            if (fieldDefCache.has(parentId)) {
              parentId = fieldDefCache.get(parentId)?.hierarchy_config?.parent_fielddef_id;
            } else {
              const parentDef = await metadataService.getFieldDefinition(parentId, tenantId);
              fieldDefCache.set(parentId, parentDef);
              parentId = parentDef?.hierarchy_config?.parent_fielddef_id;
            }
          } catch {
            break;
          }
        }
      }

      if (!cancelled) {
        setAncestorFields(Array.from(ancestors));
      }
    };

    discoverAncestors();
    return () => {
      cancelled = true;
    };
  }, [enabled, fields, tenantId]);

  return ancestorFields;
};

export default useHierarchyAncestorFields;
